import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useApp from "../../hooks/useApp.hook";
import useCart from "../../hooks/useCart.hook";
import usePagoOnline from "../../hooks/usePagoOnline.hook";
import Api from "../../utils/api";
import IServicioIndividual from "../../interfaces/IServicioIndividual";
import IModalidadPago from "../../interfaces/IModalidadPago";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faExclamationCircle,
  faInbox,
} from "@fortawesome/free-solid-svg-icons";
import "../../components/serviciosIndividuales/ServiciosPreViaje.css";
import CartCard from "../../components/serviciosIndividuales/CartCard";
import CartFooter from "../../components/serviciosIndividuales/CartFooter";
import CartDrawer from "../../components/serviciosIndividuales/CartDrawer";
import { brandName } from "../../utils/brand";

function CardSkeletonGroup() {
  return (
    <div className="mxd-cards">
      {[0, 1, 2].map((i) => (
        <div key={i} className="mxd-card-skeleton" />
      ))}
    </div>
  );
}

function ErrorScreen() {
  return (
    <div className="mxd-error">
      <div className="mxd-error-card">
        <FontAwesomeIcon icon={faExclamationCircle} className="mxd-error-icon" />
        <h5 className="mxd-error-title">No pudimos cargar tus servicios</h5>
        <p className="mxd-error-text">Por favor, intentá nuevamente más tarde.</p>
      </div>
    </div>
  );
}

function EmptyScreen() {
  return (
    <div className="mxd-empty">
      <div className="mxd-empty-icon">
        <FontAwesomeIcon icon={faInbox} />
      </div>
      <h5 className="mxd-empty-title">Sin servicios disponibles</h5>
      <p className="mxd-empty-text">
        Cuando haya servicios extras para sumar a tu viaje los vas a ver acá.
      </p>
    </div>
  );
}

function FirstAddHint({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="mxd-hint" role="status">
      <FontAwesomeIcon icon={faCircleCheck} />
      Listo. Cuando termines, tocá <strong style={{ marginLeft: 4 }}>Pagar</strong>.
    </div>
  );
}

export default function ComprarServicio() {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [serviciosIndividuales, setServiciosIndividuales] =
    useState<IServicioIndividual[]>([]);
  const [onload, error, jwt, id, pcoPasajero] = useApp();
  const [showDrawer, setShowDrawer] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showFirstAddHint, setShowFirstAddHint] = useState(false);
  const [searchParams] = useSearchParams();
  const platform = searchParams.get("platform") ?? "ios";

  const cart = useCart(pcoPasajero);
  const pago = usePagoOnline(jwt);

  useEffect(() => {
    const e = document.querySelector('meta[name="viewport"]');
    e?.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
    );
  }, []);

  useEffect(() => {
    if (!jwt) return;
    setIsLoadingData(true);

    Api("consumir", jwt)
      .post({
        param: { pnu_documento: id },
        servicio: "paxSerIndividual",
      })
      .then((res) => {
        setServiciosIndividuales(
          (res.PREF_SERIND ?? []).map((e: any) => ({
            ...e,
            BOT_IMPRIMIR: e.BOT_IMPRIMIR === "S",
            IMP_PAGAR: Number(e.IMP_PAGAR),
          }))
        );
      })
      .finally(() => setIsLoadingData(false));
  }, [jwt, id]);

  const handleToggle = (servicio: IServicioIndividual, state: boolean) => {
    const wasEmpty = cart.count === 0;
    cart.toggle(servicio, state);
    if (state && wasEmpty) {
      setShowFirstAddHint(true);
      window.setTimeout(() => setShowFirstAddHint(false), 3500);
    }
  };

  // Paso 1: pedir las modalidades de pago disponibles.
  const handleContinue = async () => {
    if (!cart.count) {
      setApiError("Sumá al menos un ítem al carrito");
      setShowDrawer(true);
      return;
    }
    setApiError("");

    const ptalones = cart.items.map((e) => e.NU_TALON).join(",");
    await pago.fetchModalidades({
      prm_co_pasajero: pcoPasajero,
      prm_talones: ptalones,
      prm_importe_ctl: cart.total,
      urls: {
        url_success: window.location.origin + "/pagoResultado",
        url_cancel: window.location.origin + "/app/comprar-servicio",
        url_redirect: window.location.origin + "/pagoResultado",
      },
    });
  };

  // Paso 2: elegida la recaudadora, iniciar el pago y redirigir a la recaudadora.
  // Navega dentro del propio WebView (como la web), sin depender de la app nativa.
  const handleSelectModalidad = async (modalidad: IModalidadPago) => {
    const url = await pago.iniciarPago(modalidad);
    if (!url) return;

    // El pago fue iniciado: vaciamos el carrito antes de salir a la recaudadora.
    cart.clear();
    window.location.href = url;
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    pago.reset();
    setApiError("");
  };

  const sortedServices = useMemo(() => {
    return [...serviciosIndividuales].sort((a, b) => {
      const aSel = cart.has(a.NU_TALON) ? 0 : 1;
      const bSel = cart.has(b.NU_TALON) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      // Próximos vencimientos primero (DD/MM/YYYY → orden ASC)
      const parseDate = (s?: string) => {
        if (!s) return Number.MAX_SAFE_INTEGER;
        const [d, m, y] = s.split("/").map(Number);
        return new Date(y < 100 ? y + 2000 : y, (m || 1) - 1, d || 1).getTime();
      };
      return parseDate(a.F_VENCIMIENTO) - parseDate(b.F_VENCIMIENTO);
    });
  }, [serviciosIndividuales, cart]);

  if (onload) {
    return (
      <div className="mxd-page">
        <div className="mxd-header">
          <span className="mxd-header-eyebrow">{brandName}</span>
          <h1 className="mxd-header-title">Adquirir servicios</h1>
          <p className="mxd-header-subtitle">Cargando tus opciones...</p>
        </div>
        <div className="mxd-section">
          <CardSkeletonGroup />
        </div>
      </div>
    );
  }
  if (error) return <ErrorScreen />;

  const hasServices = serviciosIndividuales.length > 0;

  return (
    <div className="mxd-page">
      <header className="mxd-header">
        <span className="mxd-header-eyebrow">{brandName}</span>
        <h1 className="mxd-header-title">Sumá más a tu viaje</h1>
        <p className="mxd-header-subtitle">
          Excursiones, comidas y experiencias para hacer tu viaje único.
        </p>
        {hasServices ? (
          <div className="mxd-header-stats">
            <div className="mxd-stat">
              <div className="mxd-stat-label">Disponibles</div>
              <div className="mxd-stat-value">
                {serviciosIndividuales.length}
              </div>
            </div>
            <div className="mxd-stat">
              <div className="mxd-stat-label">En carrito</div>
              <div className="mxd-stat-value">{cart.count}</div>
            </div>
          </div>
        ) : null}
      </header>

      {apiError ? <div className="mxd-error-banner">{apiError}</div> : null}

      <FirstAddHint visible={showFirstAddHint} />

      <section className="mxd-section">
        {isLoadingData ? (
          <CardSkeletonGroup />
        ) : !hasServices ? (
          <EmptyScreen />
        ) : (
          <>
            <div className="mxd-section-header">
              <h2 className="mxd-section-title">Servicios para vos</h2>
              <span className="mxd-section-count">
                {sortedServices.length}{" "}
                {sortedServices.length === 1 ? "opción" : "opciones"}
              </span>
            </div>
            <div className="mxd-cards">
              {sortedServices.map((servicio) => (
                <CartCard
                  key={servicio.NU_TALON}
                  servicio={servicio}
                  selected={cart.has(servicio.NU_TALON)}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <CartFooter
        count={cart.count}
        total={cart.total}
        onOpenCart={() => {
          setApiError("");
          setShowDrawer(true);
        }}
        onPay={() => {
          setApiError("");
          setShowDrawer(true);
        }}
        isAndroid={platform === "android"}
        disabled={pago.loadingModalidades || pago.payingToken != null}
      />

      <CartDrawer
        show={showDrawer}
        items={cart.items}
        total={cart.total}
        onClose={handleCloseDrawer}
        onRemove={cart.remove}
        onContinue={handleContinue}
        loadingModalidades={pago.loadingModalidades}
        modalidades={pago.modalidades}
        onSelectModalidad={handleSelectModalidad}
        payingToken={pago.payingToken}
        onBack={pago.reset}
        apiError={apiError || pago.error}
      />
    </div>
  );
}
